import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import AccommodationPopup from "@/components/AccommodationPopup";
import { useAuth } from "@/contexts/AuthContext";
import { motion, useReducedMotion } from "framer-motion";
import { Flag, getCountry3Letter } from "@/lib/countries";
import { Bubbles, LaneLine, Caustics } from "@/components/SwimmingFX";
import { displayEntryTime } from "@/lib/entryTime";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar, CreditCard, Hotel, Plane, Bell, Download,
  CheckCircle2, AlertTriangle, ArrowRight, FileText,
  IdCard as IdCardIcon, Map, ChevronRight, BarChart2, Pencil,
  XCircle, Clock, List, Users, Video, Award
} from "lucide-react";

// Per-event delegation approval status, shown once a registration is paid —
// "pending" until the country delegate reviews it, then approved/rejected.
const DelegationStatusBadge = ({ status }) => {
  if (status === "approved") {
    return (
      <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-[#00E676]">
        <CheckCircle2 className="w-3 h-3" /> Approved by delegation
      </span>
    );
  }
  if (status === "rejected") {
    return (
      <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-[#FF3B30]">
        <XCircle className="w-3 h-3" /> Not approved by delegation
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold text-[#94A3B8]">
      <Clock className="w-3 h-3" /> Pending delegation review
    </span>
  );
};

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.35, delay, ease: [0.16, 1, 0.3, 1] },
});



const SHIRT_SIZES = ["XS", "S", "M", "L", "XL", "XXL", "3XL"];
const AADHAAR_RE = /^\d{12}$/;

// Once an Indian athlete has a paid registration, their "club" is replaced
// with their state -- membership in this list doubles as the "already done"
// check (no separate migrated flag needed: club_team is freely editable via
// PUT /profile, and no real club name is going to exactly match a state).
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa",
  "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
  "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland",
  "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
  "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const needsShirtSize = (u) => !!u && !u.shirt_size;
const needsSFID = (u) => !!u && u.nationality === "India" && (!u.identification_number || AADHAAR_RE.test(u.identification_number));
// SFI UID / passport number is editable any time before verification (locked
// after, to stop a verified identity being swapped post-verification) —
// applies to every nationality, not just India. Distinct from needsSFID,
// which is about whether the current value looks missing/invalid.
const canEditIdentificationNumber = (u) => !!u && (!u.documents_verified || needsSFID(u));

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [sfiUploading, setSfiUploading] = useState(false);
  const [data,         setData]         = useState(null);
  const [eventSchedule, setEventSchedule] = useState({});
  const [accommodationOpen, setAccommodationOpen] = useState(true);
  const [eventRegistrationOpen, setEventRegistrationOpen] = useState(true);
  const [logisticsOpen, setLogisticsOpen] = useState(true); // global flag — logistics is closed for everyone regardless of nationality
  const [loading, setLoading] = useState(true);
  const [heatlists, setHeatlists] = useState([]);
  const [cert, setCert] = useState(null);   // {open, eligible} participation certificate
  const [loadingHeats, setLoadingHeats] = useState(true);
  const shouldReduceMotion = useReducedMotion();

  const load = useCallback(async () => {
    try {
      const [dashRes, schedRes, metaRes, heatsRes, certRes] = await Promise.allSettled([
        api.get("/dashboard/summary"),
        api.get("/schedule"),
        api.get("/meta/events"),
        api.get("/heatlists/mine"),
        api.get("/certificates/me"),
      ]);
      if (certRes.status === "fulfilled") setCert(certRes.value.data);
      if (dashRes.status === "fulfilled") setData(dashRes.value.data);
      else toast.error("Failed to load dashboard");
      if (metaRes.status === "fulfilled") {
        setAccommodationOpen(!!metaRes.value.data?.accommodation_open);
        setEventRegistrationOpen(!!metaRes.value.data?.event_registration_open);
        setLogisticsOpen(!!metaRes.value.data?.registration_open);
      }
      if (heatsRes.status === "fulfilled") setHeatlists(heatsRes.value.data);
      setLoadingHeats(false);

      if (schedRes.status === "fulfilled") {
        const map = {};
        for (const day of (schedRes.value.data?.days || [])) {
          for (const session of (day.sessions || [])) {
            for (const ev of (session.events || [])) {
              if (!map[ev.name]) {
                map[ev.name] = {
                  label:   day.label,
                  date:    day.date,
                  session: session.session,
                  time:    session.time,
                };
              }
            }
          }
        }
        setEventSchedule(map);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    if (refresh) refresh();
  }, [load, refresh]);

  const [promptOpen,     setPromptOpen]     = useState(false);
  const [shirtSizeInput, setShirtSizeInput] = useState("");
  const [sfidInput,      setSfidInput]      = useState("");
  const [savingPrompt,   setSavingPrompt]   = useState(false);

  const termsModalOpen = !!user && user.documents_verified && !user.has_accepted_terms;
  const [termsCheckbox, setTermsCheckbox] = useState(false);
  const [privacyCheckbox, setPrivacyCheckbox] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);

  const submitTerms = async () => {
    if (!termsCheckbox || !privacyCheckbox) {
      toast.error("You must accept both policies to proceed.");
      return;
    }
    setSavingTerms(true);
    try {
      await api.put("/profile", { has_accepted_terms: true });
      await refresh();
      toast.success("Agreements accepted successfully.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to accept agreements");
    } finally {
      setSavingTerms(false);
    }
  };

  const [stateInput, setStateInput] = useState("");
  const [savingState, setSavingState] = useState(false);

  const submitState = async () => {
    if (!stateInput) {
      toast.error("Select your state to continue.");
      return;
    }
    setSavingState(true);
    try {
      await api.put("/profile", { club_team: stateInput });
      await refresh();
      toast.success("State saved.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save state");
    } finally {
      setSavingState(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    const ineligibleNow = user.masters_category === "Under 25 (Not Eligible)";
    if (!ineligibleNow && (needsShirtSize(user) || needsSFID(user))) {
      setPromptOpen(true);
    }
  }, [user]);

  // Pre-fill the edit dialog with current values whenever it opens, so
  // clicking "Edit" on an already-set field shows what's there instead of
  // a blank input.
  useEffect(() => {
    if (promptOpen) {
      setShirtSizeInput(user?.shirt_size || "");
      setSfidInput(user?.identification_number || "");
    }
  }, [promptOpen, user]);

  const submitPrompt = async () => {
    const payload = {};
    if (!shirtSizeInput) {
      toast.error("Select a shirt size");
      return;
    }
    payload.shirt_size = shirtSizeInput;
    if (canEditIdentificationNumber(user)) {
      if (!sfidInput.trim()) {
        toast.error(user?.nationality === "India" ? "Enter your SFI UID" : "Enter your passport number");
        return;
      }
      if (user?.nationality === "India" && /^\d{12}$/.test(sfidInput.trim())) {
        toast.error("Please enter your SFI UID, not your Aadhaar number.");
        return;
      }
      payload.identification_number = sfidInput.trim();
    }
    setSavingPrompt(true);
    try {
      await api.put("/profile", payload);
      await refresh();
      toast.success("Profile updated");
      setPromptOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update profile");
    } finally {
      setSavingPrompt(false);
    }
  };

  const viewInvoice = (p) => {
    if (p.target_type === "accommodation") navigate(`/invoice/accommodation/${p.target_id}`);
    else if (p.target_type === "logistics") navigate(`/invoice/logistics/${p.target_id}`);
    else if (p.target_type === "meal") navigate(`/invoice/meals/${p.target_id}`);
    else if (p.target_type === "relay_team") navigate(`/invoice/relay/${p.payment_id}`);
    else navigate(`/invoice/registration/${p.payment_id}`);
  };

  if (loading) {
    return (
      <div className="min-h-dvh bg-[#0B1429]">
        <Navbar />
        <div className="h-20" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-10 space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 rounded-2xl bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const allRegs    = data?.registrations || [];
  const unpaidReg  = allRegs.find(r => r.payment_status !== "paid") || null;
  const reg        = unpaidReg || allRegs[0] || null;  // kept for backward compat
  const paid       = allRegs.length > 0 && allRegs.every(r => r.payment_status === "paid");
  const allEvents  = [...new Set(allRegs.flatMap(r => r.events       || []))];
  const allRelays  = [...new Set(allRegs.flatMap(r => r.relay_events || []))];
  const allEntryTimes = Object.assign({}, ...allRegs.map(r => r.entry_times || {}));
  // Delegation review status is only meaningful for paid registrations —
  // events on an unpaid registration haven't reached the delegate yet.
  const allEventStatuses = Object.assign({}, ...allRegs.filter(r => r.payment_status === "paid").map(r => r.event_statuses || {}));
  const isEventPaid = (e) => allRegs.some(r => r.payment_status === "paid" && (r.events?.includes(e) || r.relay_events?.includes(e)));
  const totalEventCount = allEvents.length;

  const ineligible = user?.masters_category === "Under 25 (Not Eligible)";
  const verificationStatus = user?.verification_status || (user?.documents_verified ? "verified" : "pending");
  const docsPending = !user?.documents_verified;
  const hasPaidAccommodation = !!data?.accommodations?.some(a => a.payment_status === "paid");
  const unpaidAccommodation = data?.accommodations?.find(a => a.payment_status !== "paid") || null;
  // International athletes must book + pay for a hotel before event
  // registration unlocks; accommodationOpen without eventRegistrationOpen
  // means exactly that (see is_event_registration_open_for in config.py).
  const needsHotelBeforeEvents = accommodationOpen && !eventRegistrationOpen && !hasPaidAccommodation;

  const isNonAthlete = user?.participant_type === "Coach" || user?.participant_type === "Manager";
  const isIndia = user?.nationality === "India";
  const sfiStatus = user?.sfi_results_status;

  // Indian athletes (not Coach/Manager) with a paid registration must pick
  // their state on next login -- one-time, blocking, replaces club_team.
  const stateModalOpen = !!user && user.has_accepted_terms && isIndia && !isNonAthlete
    && !INDIAN_STATES.includes(user.club_team)
    && allRegs.some(r => r.payment_status === "paid");

  const onSfiUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5MB"); return; }
    setSfiUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload/document", {
        method: "POST",
        headers: { "X-Requested-With": "XMLHttpRequest" },
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Upload failed");
      }
      const { url } = await res.json();
      await api.post("/sfi-results", { document_url: url });
      toast.success("SFI results submitted for review");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Upload failed");
    } finally {
      setSfiUploading(false);
      e.target.value = "";
    }
  };

  const nextAction = (() => {
    if (ineligible) return null;
    if (cert?.open && cert?.eligible) return { label: "Download Certificate", to: "/certificate", note: "Your participation certificate is ready" };
    if (verificationStatus === "rejected") return { label: "Re-upload Documents", to: "/profile/documents", note: "Action required — see rejection reason above" };
    if (docsPending) return { label: "Upload Documents", to: "/profile/documents", note: "Verification required before event registration" };
    if (needsHotelBeforeEvents) {
      if (unpaidAccommodation) return { label: "Complete Hotel Payment", to: "/accommodation", note: "Pay for your hotel to unlock event registration" };
      if (user?.nationality !== "India") return { label: "Accommodation Selection", to: "/accommodation-choice", note: "Select official hotel or opt-out" };
      return { label: "Book a Hotel", to: "/accommodation", note: "Start by booking your hotel" };
    }
    if (allRegs.length === 0 && eventRegistrationOpen) return { label: "Register for Events", to: "/events", note: "Secure your spot — registration is open" };
    if (unpaidReg) {
      if (!hasPaidAccommodation) {
        if (unpaidAccommodation) {
          return { label: "Complete Checkout", to: `/payment?registration_id=${unpaidReg.registration_id}&booking_id=${unpaidAccommodation.booking_id}`, note: "Complete payment for events and hotel" };
        }
        if (user?.nationality !== "India") {
          return { label: "Accommodation Selection", to: `/accommodation-choice?cart_reg_id=${unpaidReg.registration_id}`, note: "Select official hotel or opt-out" };
        }
        // Accommodation is optional for India — go straight to paying for the registration itself.
        return { label: "Complete Payment", to: `/payment/${unpaidReg.registration_id}`, note: `${unpaidReg.currency === "INR" ? "₹" : "$"}${unpaidReg.amount_usd} ${unpaidReg.currency || "USD"} outstanding` };
      }
      return { label: "Complete Payment", to: `/payment/${unpaidReg.registration_id}`, note: `${unpaidReg.currency === "INR" ? "₹" : "$"}${unpaidReg.amount_usd} ${unpaidReg.currency || "USD"} outstanding` };
    }
    if (!data?.accommodations?.length && accommodationOpen) return { label: "Book Accommodation", to: "/accommodation", note: "Hotels near the venue are filling fast" };
    return null;
  })();

  return (
    <div className="min-h-dvh bg-[#0B1429] text-white">
      <Navbar />
      <AccommodationPopup />
      <div className="h-20" />

      <Dialog open={termsModalOpen}>
        <DialogContent className="bg-[#142040] border-white/10 text-white [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Required Agreements</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Before proceeding to your dashboard, please review and accept our Privacy Policy and Terms and Conditions. This is a one-time requirement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 my-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input 
                type="checkbox" 
                checked={privacyCheckbox} 
                onChange={(e) => setPrivacyCheckbox(e.target.checked)} 
                className="mt-1 w-4 h-4 accent-[#00F0FF] cursor-pointer shrink-0" 
              />
              <span className="text-sm text-[#CBD5E1] group-hover:text-white transition-colors">
                I have read and accept the <a href="/privacy-policy" target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline" onClick={(e) => e.stopPropagation()}>Privacy Policy</a>.
              </span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group">
              <input 
                type="checkbox" 
                checked={termsCheckbox} 
                onChange={(e) => setTermsCheckbox(e.target.checked)} 
                className="mt-1 w-4 h-4 accent-[#00F0FF] cursor-pointer shrink-0" 
              />
              <span className="text-sm text-[#CBD5E1] group-hover:text-white transition-colors">
                I have read and accept the <a href="/terms-and-conditions" target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline" onClick={(e) => e.stopPropagation()}>Terms and Conditions</a>.
              </span>
            </label>
          </div>
          <DialogFooter>
            <button
              disabled={savingTerms || !privacyCheckbox || !termsCheckbox}
              onClick={submitTerms}
              className="btn-primary disabled:opacity-50 w-full"
            >
              {savingTerms ? "Saving..." : "Accept & Continue"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stateModalOpen}>
        <DialogContent className="bg-[#142040] border-white/10 text-white [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Select Your State</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              Please select the Indian state you represent. This replaces your club/team field and is a one-time requirement.
            </DialogDescription>
          </DialogHeader>
          <div className="my-2">
            <select
              value={stateInput}
              onChange={(e) => setStateInput(e.target.value)}
              className="w-full h-10 bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 text-sm text-white focus:outline-none focus:border-[#00F0FF]/40 transition-colors appearance-none"
            >
              <option value="" className="text-black">Select a state</option>
              {INDIAN_STATES.map(s => (
                <option key={s} value={s} className="text-black">{s}</option>
              ))}
            </select>
          </div>
          <DialogFooter>
            <button
              disabled={savingState || !stateInput}
              onClick={submitState}
              className="btn-primary disabled:opacity-50 w-full"
            >
              {savingState ? "Saving…" : "Save & Continue"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={promptOpen} onOpenChange={setPromptOpen}>
        <DialogContent className="bg-[#142040] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{needsShirtSize(user) || needsSFID(user) ? "Complete your profile" : "Edit profile details"}</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              {needsShirtSize(user) || needsSFID(user)
                ? "A couple of details are needed before you can proceed."
                : "Update your shirt size or SFI UID below."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">Shirt Size</label>
              <select
                data-testid="prompt-shirt-size"
                value={shirtSizeInput}
                onChange={(e) => setShirtSizeInput(e.target.value)}
                className="input-dark"
              >
                <option value="">Select size</option>
                {SHIRT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            {canEditIdentificationNumber(user) && (
              <div>
                <label className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                  {user?.nationality === "India" ? "SFI UID" : "Passport Number"}
                </label>
                <input
                  type="text"
                  data-testid="prompt-sfid"
                  value={sfidInput}
                  onChange={(e) => setSfidInput(e.target.value)}
                  placeholder={user?.nationality === "India" ? "Enter your SFI UID" : "Enter your passport number"}
                  className="input-dark"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <button
              data-testid="prompt-submit"
              disabled={savingPrompt}
              onClick={submitPrompt}
              className="btn-primary disabled:opacity-50"
            >
              {savingPrompt ? "Saving..." : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Video Hero ── */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <video
          autoPlay loop muted playsInline
          poster="https://images.pexels.com/photos/18090677/pexels-photo-18090677.jpeg"
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/hero_compressed.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1429] via-[#0B1429]/75 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B1429] via-[#0B1429]/20 to-transparent" />

        {!shouldReduceMotion && (
          <>
            <Caustics opacity={0.12} />
            <Bubbles count={8} />
          </>
        )}

        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto w-full px-6 lg:px-10 pb-10">
            <motion.div {...fade(0.1)}>
              <div className="mb-4 inline-block">
                <div
                  className="relative inline-flex items-center gap-2.5 px-4 sm:px-5 py-2 rounded-full bg-[#0B1429]/70 border border-[#00E676]/40 backdrop-blur-md shadow-[0_0_15px_rgba(0,230,118,0.2)] overflow-hidden max-w-full"
                >
                  <span className="w-2 h-2 rounded-full bg-[#00E676] shrink-0 animate-pulse" />
                  <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wide sm:tracking-widest text-white/90">
                    Best of luck to all swimmers competing in the championships!
                  </span>
                  <span className="badge-shimmer" />
                </div>
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#00F0FF] mb-2">
                Athlete Dashboard
              </p>
              <h1 className="font-display font-black text-3xl md:text-5xl leading-tight">
                {user?.full_name}
              </h1>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {user?.nationality && (
                  <span className="flex items-center gap-1.5 text-sm text-[#CBD5E1]">
                    <Flag country={user.nationality} size={16} />
                    <span className="hidden sm:inline">{user.nationality}</span>
                    <span className="inline sm:hidden">{getCountry3Letter(user.nationality)}</span>
                  </span>
                )}
                {user?.masters_category && (
                  <span className="text-sm text-[#CBD5E1]">· Masters {user.masters_category}</span>
                )}
                {paid && <Pill color="green">Confirmed</Pill>}
                {reg && !paid && <Pill color="yellow">Payment Pending</Pill>}
                {docsPending && !ineligible && verificationStatus === "rejected" && <Pill color="red">Verification Not Approved</Pill>}
                {docsPending && !ineligible && verificationStatus !== "rejected" && <Pill color="yellow">Verification Pending</Pill>}
                {ineligible && <Pill color="red">Not Eligible</Pill>}
              </div>
              {!shouldReduceMotion && (
                <div className="mt-5 max-w-xs hidden md:block">
                  <LaneLine />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">

        {/* Quick Nav — plain text links */}
        <motion.div
          {...fade(0.15)}
          className="flex items-center gap-6 overflow-x-auto pb-2 mb-8 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] border-b border-white/[0.05]"
        >
          {[
            { to: "/events",            icon: Calendar,   label: isNonAthlete ? "Registration" : "Events",            off: ineligible || docsPending },
            { to: "/relays",            icon: Users,      label: "Relays",            off: ineligible || docsPending },
            { to: (user?.nationality !== "India" && !hasPaidAccommodation && !unpaidAccommodation) ? `/accommodation-choice${unpaidReg ? `?cart_reg_id=${unpaidReg.registration_id}` : ""}` : "/accommodation",     icon: Hotel,      label: "Hotel" },
            { to: "/logistics",         icon: Plane,      label: "Logistics" },
            { to: "/meals",             icon: List,       label: "Meals", off: ineligible || docsPending },
            { to: "/profile/documents", icon: FileText,   label: "Documents" },
            { to: "/id-card",           icon: IdCardIcon, label: "ID Card",            accent: user?.can_generate_id, off: !user?.can_generate_id },
            { to: "/certificate",       icon: Award,      label: "Certificate",        accent: cert?.open && cert?.eligible, off: !(cert?.open && cert?.eligible) },
            { to: "/city-guide",        icon: Map,        label: "Explore Bengaluru" },
          ].map(({ to, icon: Icon, label, off, accent }) => (
            <Link
              key={label}
              to={off ? "#" : to}
              aria-disabled={off}
              className={[
                "flex items-center gap-1.5 shrink-0 pb-3 text-sm font-medium transition-colors duration-150 touch-manipulation border-b-2 -mb-px",
                off
                  ? "text-white/20 cursor-not-allowed pointer-events-none border-transparent"
                  : accent
                  ? "text-[#00F0FF] hover:text-white border-[#00F0FF]/60 cursor-pointer"
                  : "text-[#94A3B8] hover:text-white border-transparent hover:border-white/20 cursor-pointer",
              ].join(" ")}
            >
              <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
              {label}
            </Link>
          ))}
        </motion.div>

        {/* Two-column layout */}
        <div className="grid grid-cols-12 gap-6 lg:gap-8">

          {/* ── Sidebar ── */}
          <aside className="col-span-12 md:col-span-3">
            <motion.div
              {...fade(0.2)}
              className="sticky top-28 rounded-3xl bg-[#142040]/50 backdrop-blur-md border border-white/[0.08] overflow-hidden"
            >
              {/* Ambient glow top */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-36 h-16 bg-[#00F0FF]/[0.08] blur-3xl pointer-events-none" />

              <div className="relative p-6">
                <div className="flex flex-col items-center text-center mb-6">
                  {user?.profile_photo ? (
                    <img
                      src={user.profile_photo}
                      alt={user?.full_name}
                      className="w-20 h-20 rounded-full object-cover ring-2 ring-[#00F0FF]/30 mb-4"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#1D3461] to-[#0B1429] border border-[#00F0FF]/20 flex items-center justify-center text-2xl font-black text-[#00F0FF] mb-4">
                      {user?.full_name?.[0]}
                    </div>
                  )}
                  <p className="text-base font-bold leading-tight">{user?.full_name}</p>
                  <p className="text-xs text-[#94A3B8] mt-0.5 max-w-full truncate">{user?.email}</p>
                  {user?.masters_category && (
                    <span className="mt-3 inline-block px-3 py-1 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/20 text-[#00F0FF] text-[11px] font-semibold tracking-wide">
                      {user.masters_category}
                    </span>
                  )}
                </div>

                <div className="space-y-3 text-sm border-t border-white/[0.06] pt-5">
                  {[
                    { label: "Gender", value: user?.gender },
                    { label: "DOB",    value: user?.date_of_birth },
                    { label: "Club",   value: user?.club_team },
                  ].map(({ label, value }) =>
                    value ? (
                      <div key={label} className="flex items-center justify-between gap-2">
                        <span className="text-[11px] uppercase tracking-[0.12em] text-[#64748B]">{label}</span>
                        <span className="text-xs text-[#CBD5E1] truncate">{value}</span>
                      </div>
                    ) : null
                  )}
                  {user?.nationality && (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] uppercase tracking-[0.12em] text-[#64748B]">Country</span>
                      <span className="flex items-center gap-1.5 text-xs text-[#CBD5E1]">
                        <Flag country={user.nationality} size={14} />
                        <span className="hidden sm:inline">{user.nationality}</span>
                        <span className="inline sm:hidden">{getCountry3Letter(user.nationality)}</span>
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] uppercase tracking-[0.12em] text-[#64748B]">Shirt Size</span>
                    <span className="flex items-center gap-1.5 text-xs text-[#CBD5E1]">
                      {user?.shirt_size || "—"}
                      <button
                        data-testid="edit-shirt-size"
                        onClick={() => setPromptOpen(true)}
                        className="p-3.5 -m-3.5 text-[#00F0FF] hover:text-white transition-colors"
                        aria-label="Edit shirt size"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] uppercase tracking-[0.12em] text-[#64748B]">
                      {user?.nationality === "India" ? "SFI UID" : "Passport No."}
                    </span>
                    <span className={`flex items-center gap-1.5 text-xs truncate ${needsSFID(user) ? "text-[#FBBF24]" : "text-[#CBD5E1]"}`}>
                      {needsSFID(user) && <AlertTriangle className="w-3 h-3 shrink-0" />}
                      {user?.identification_number || "Not set"}
                      {canEditIdentificationNumber(user) && (
                        <button
                          data-testid="edit-sfid"
                          onClick={() => setPromptOpen(true)}
                          className="text-[#00F0FF] hover:text-white transition-colors"
                          aria-label={user?.nationality === "India" ? "Edit SFI UID" : "Edit passport number"}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </aside>

          {/* ── Main ── */}
          <main className="col-span-12 md:col-span-9 space-y-5">

            {ineligible && (
              <motion.div {...fade(0.2)}>
                <AlertBanner color="red" title="Not Eligible to Compete">
                  Per AMSC India 2026 rules, you must be 25 or older as of Dec 31, 2026.
                </AlertBanner>
              </motion.div>
            )}
            {!ineligible && verificationStatus === "rejected" && (
              <motion.div {...fade(0.2)}>
                <AlertBanner color="red" title="Verification Not Approved">
                  {user?.verification_reason || "Your documents were not approved by the organiser."} Please re-upload the requested documents.
                  <a href="/medical-certificate-draft.pdf" target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-[#00F0FF] hover:text-white transition-colors">
                    <FileText className="w-3.5 h-3.5" /> Download Sample Medical Certificate
                  </a>
                </AlertBanner>
              </motion.div>
            )}
            {!ineligible && verificationStatus === "pending" && (
              <motion.div {...fade(0.2)}>
                <AlertBanner color={user?.rejection_reason ? "red" : "yellow"} title={user?.rejection_reason ? "Documents Verification Not Approved" : "Documents Pending Verification"}>
                  {user?.rejection_reason ? (
                    <span>
                      Your profile verification was not approved: <strong className="text-white font-semibold">{user.rejection_reason}</strong>. Please re-upload valid documents.
                      <a href="/medical-certificate-draft.pdf" target="_blank" rel="noopener noreferrer" className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#00F0FF] hover:text-white transition-colors">
                        <FileText className="w-3.5 h-3.5" /> Download Sample Medical Certificate
                      </a>
                    </span>
                  ) : (
                    "Verification takes 1–2 working days. Event registration opens once your documents are verified."
                  )}
                </AlertBanner>
              </motion.div>
            )}

            {/* Next action — glowing CTA */}
            {nextAction && (
              <motion.div
                {...fade(0.25)}
                className="relative overflow-hidden rounded-2xl border border-[#00F0FF]/25 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                style={{ background: "linear-gradient(135deg, rgba(0,240,255,0.07) 0%, rgba(0,240,255,0.02) 100%)" }}
              >
                <div className="absolute -left-8 top-1/2 -translate-y-1/2 w-48 h-48 bg-[#00F0FF]/[0.06] blur-3xl rounded-full pointer-events-none" />
                <div className="relative">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#00F0FF] mb-1">Next Step</p>
                  <p className="text-base font-bold">{nextAction.label}</p>
                  <p className="text-xs text-[#94A3B8] mt-0.5">{nextAction.note}</p>
                </div>
                <Link
                  to={nextAction.to}
                  className="relative shrink-0 flex items-center gap-2 h-10 px-5 rounded-xl bg-[#00F0FF] text-[#0B1429] text-sm font-bold hover:bg-white transition-colors duration-200 touch-manipulation cursor-pointer"
                >
                  {nextAction.label} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            )}

            {/* ── SFI National Masters Results — India only, a second
                verification stage on top of the base document check above.
                Event registration (and accommodation, optionally) unlock
                once this is admin-approved — see is_event_registration_open_for. ── */}
            {!ineligible && !docsPending && isIndia && (
              <motion.div {...fade(0.22)}>
                <div className="rounded-2xl bg-[#142040]/40 backdrop-blur-sm border border-white/[0.08] overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#94A3B8]" strokeWidth={1.5} />
                      <span className="text-sm font-semibold">SFI National Masters Results</span>
                    </div>
                    {sfiStatus === "approved" && (
                      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#10B981]">
                        <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Approved
                      </span>
                    )}
                    {sfiStatus === "pending" && (
                      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#FBBF24]">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} /> Under Review
                      </span>
                    )}
                    {sfiStatus === "rejected" && (
                      <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#EF4444]">
                        <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Not Approved
                      </span>
                    )}
                  </div>
                  <div className="px-6 py-5">
                    {sfiStatus === "approved" ? (
                      <p className="text-sm text-[#94A3B8]">Verified — you're cleared to register for events. Accommodation is also open to you, though optional.</p>
                    ) : sfiStatus === "pending" ? (
                      <p className="text-sm text-[#94A3B8]">Submitted — verification takes 1–2 working days. You'll be notified once it's reviewed.</p>
                    ) : (
                      <>
                        {sfiStatus === "rejected" && (
                          <p className="text-sm text-[#EF4444] mb-3">
                            {user.sfi_results_reason || "Not approved."} Please re-upload a valid document.
                          </p>
                        )}
                        <p className="text-xs text-[#94A3B8] mb-3">Upload your SFI National Masters Results document to unlock event registration.</p>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={onSfiUpload} id="sfi-results-upload" className="hidden" disabled={sfiUploading} />
                        <label htmlFor="sfi-results-upload" className="h-9 px-4 rounded-lg bg-[#00F0FF] text-[#0B1429] text-xs font-bold hover:bg-white transition-colors duration-200 inline-flex items-center gap-2 cursor-pointer touch-manipulation">
                          {sfiUploading ? "Uploading…" : (sfiStatus === "rejected" ? "Re-upload Document" : "Upload Document")}
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
            {!ineligible && !docsPending && allRegs.length === 0 && !eventRegistrationOpen && !needsHotelBeforeEvents && (!isIndia || sfiStatus === "approved") && (
              <motion.div
                {...fade(0.25)}
                className="relative overflow-hidden rounded-2xl border border-[#00F0FF]/25 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4"
                style={{ background: "linear-gradient(135deg, rgba(0,240,255,0.07) 0%, rgba(0,240,255,0.02) 100%)" }}
              >
                <div className="absolute -left-8 top-1/2 -translate-y-1/2 w-48 h-48 bg-[#00F0FF]/[0.06] blur-3xl rounded-full pointer-events-none" />
                <div className="relative">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#00F0FF] mb-1">You're Verified</p>
                  <p className="text-base font-bold">Congratulations — you're all set to compete</p>
                  <p className="text-xs text-[#94A3B8] mt-0.5">Event registration hasn't opened yet; we'll let you know the moment it does.</p>
                </div>
              </motion.div>
            )}

            {/* Event Registration */}
            <motion.div
              {...fade(0.3)}
              className="rounded-2xl bg-[#142040]/40 backdrop-blur-sm border border-white/[0.08] overflow-hidden"
              data-testid="reg-card"
            >
              <div className="flex flex-wrap items-center justify-between gap-y-3 px-6 py-4 border-b border-white/[0.06]">
                <div className="flex items-center gap-2 min-w-0">
                  <Calendar className="w-4 h-4 text-[#94A3B8] shrink-0" strokeWidth={1.5} />
                  <span className="text-sm font-semibold">{isNonAthlete ? "Registration" : "Event Registration"}</span>
                  {reg && <span className="hidden sm:inline text-[11px] text-[#64748B] font-mono truncate">{reg.registration_id}</span>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {paid && (
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#10B981]">
                      <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> Confirmed
                    </span>
                  )}
                  {/* Coaches/Managers register once with a flat base fee — /events
                      shows them a plain "Register as Coach/Manager" flow there
                      (no event/relay picker). Needed once, before `reg` exists;
                      once registered there's nothing left to add or edit. */}
                  {isNonAthlete && !reg && !ineligible && !docsPending && eventRegistrationOpen && (
                    <Link to="/events" className="h-8 px-4 rounded-lg bg-[#00F0FF] text-[#0B1429] text-xs font-bold hover:bg-white transition-colors duration-200 flex items-center touch-manipulation cursor-pointer">
                      Start Registration
                    </Link>
                  )}
                  {!isNonAthlete && !ineligible && !docsPending && totalEventCount < 5 && eventRegistrationOpen && (
                    <Link to="/events" className="h-8 px-4 rounded-lg bg-[#00F0FF] text-[#0B1429] text-xs font-bold hover:bg-white transition-colors duration-200 flex items-center touch-manipulation cursor-pointer">
                      {totalEventCount > 0 ? `Add Events (${5 - totalEventCount} left)` : "Register Events"}
                    </Link>
                  )}
                  {!ineligible && !docsPending && totalEventCount === 0 && !eventRegistrationOpen && (
                    needsHotelBeforeEvents ? (
                      <Link to="/accommodation" className="text-[11px] text-[#00F0FF] hover:text-white transition-colors">Book a hotel to unlock</Link>
                    ) : (
                      <span className="text-[11px] text-[#64748B]">Not open yet</span>
                    )
                  )}
                  {totalEventCount >= 5 && (
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold text-[#10B981]">
                      <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> 5/5 Events
                    </span>
                  )}
                  {reg && !paid && (
                    <Link
                      to={
                        !hasPaidAccommodation
                          ? (unpaidAccommodation 
                              ? `/payment?registration_id=${reg.registration_id}&booking_id=${unpaidAccommodation.booking_id}` 
                              : (user?.nationality !== "India" ? `/accommodation-choice?cart_reg_id=${reg.registration_id}` : "/accommodation"))
                          : `/payment/${reg.registration_id}`
                      }
                      className="h-8 px-4 rounded-lg bg-[#FBBF24] text-[#0B1429] text-xs font-bold hover:bg-white transition-colors duration-200 flex items-center touch-manipulation cursor-pointer"
                    >
                      {!hasPaidAccommodation && !unpaidAccommodation ? (user?.nationality !== "India" ? "Select Accommodation" : "Book Hotel to Pay") : "Pay Now"}
                    </Link>
                  )}
                </div>
              </div>
              {allEvents.length > 0 || allRelays.length > 0 ? (
                <div className="px-6 py-5 flex flex-wrap gap-3">
                  {allEvents.map((e) => {
                    const sched = eventSchedule[e];
                    const entryTime = allEntryTimes[e];
                    return (
                      <div key={e} className="flex flex-col gap-0.5 px-3 py-2.5 rounded-xl bg-[#0B1429]/70 border border-white/[0.07]">
                        <span className="text-xs font-medium text-[#CBD5E1]">{e}</span>
                        <span className="text-[11px] text-[#64748B] tabular-nums">
                          {sched ? `${sched.label} · ${sched.time} · ${sched.session.charAt(0).toUpperCase() + sched.session.slice(1)}` : "Schedule TBD"}
                        </span>
                        {entryTime && (
                          <span className="text-[11px] text-[#00F0FF] font-mono tabular-nums">Entry: {displayEntryTime(entryTime)}</span>
                        )}
                        {isEventPaid(e) && <DelegationStatusBadge status={allEventStatuses[e]} />}
                      </div>
                    );
                  })}
                  {allRelays.map((e) => {
                    const sched = eventSchedule[e];
                    return (
                      <div key={e} className="flex flex-col gap-0.5 px-3 py-2.5 rounded-xl bg-[#FBBF24]/[0.08] border border-[#FBBF24]/20">
                        <span className="text-xs font-medium text-[#FBBF24] flex items-center gap-1.5">
                          <span className="text-[9px] font-bold uppercase tracking-wider opacity-70">Relay</span>{e}
                        </span>
                        <span className="text-[11px] text-[#FBBF24]/50 tabular-nums">
                          {sched ? `${sched.label} · ${sched.time} · ${sched.session.charAt(0).toUpperCase() + sched.session.slice(1)}` : "Schedule TBD"}
                        </span>
                        {isEventPaid(e) && <DelegationStatusBadge status={allEventStatuses[e]} />}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-sm text-[#64748B]">
                  {isNonAthlete ? (reg ? `Registered as ${user.participant_type}` : `You haven't started your ${user.participant_type} registration yet.`) : "No events registered yet."}
                </div>
              )}
            </motion.div>

            {/* Payments */}
            <motion.div
              {...fade(0.35)}
              className="rounded-2xl bg-[#142040]/40 backdrop-blur-sm border border-white/[0.08] overflow-hidden"
            >
              <div className="flex items-center gap-3 px-6 py-4 border-b border-white/[0.06]">
                <CreditCard className="w-4 h-4 text-[#94A3B8]" strokeWidth={1.5} />
                <span className="text-sm font-semibold">Payments</span>
              </div>
              {!data?.payments?.length ? (
                <div className="px-6 py-8 text-center text-sm text-[#64748B]">No payments yet.</div>
              ) : (
                <div className="divide-y divide-white/[0.05]">
                  {data.payments.map((p) => (
                    <div
                      key={p.payment_id}
                      data-testid={`payment-row-${p.payment_id}`}
                      className="flex items-center justify-between gap-3 px-6 py-4 hover:bg-white/[0.02] transition-colors duration-150"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold tabular-nums">
                          <span className="text-white">{p.currency === "INR" ? "₹" : "$"}{p.amount_usd}</span>
                          <span className="text-[#64748B] font-normal ml-1.5">{p.currency || "USD"}</span>
                        </p>
                        <p className="text-xs text-[#64748B] mt-0.5 truncate">
                          {p.target_type} · {p.method} · <span className="font-mono">{p.transaction_id}</span>
                        </p>
                      </div>
                      <button
                        onClick={() => viewInvoice(p)}
                        data-testid={`invoice-${p.payment_id}`}
                        className="shrink-0 flex items-center gap-1.5 py-3 -my-3 text-xs text-[#00F0FF] hover:text-white transition-colors duration-150 touch-manipulation cursor-pointer"
                      >
                        <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> Invoice
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Travel & Notifications */}
            <div className="grid md:grid-cols-2 gap-5">
              {/* Travel */}
              <motion.div
                {...fade(0.4)}
                className="rounded-2xl bg-[#142040]/40 backdrop-blur-sm border border-white/[0.08] overflow-hidden"
              >
                <div className="px-5 py-4 border-b border-white/[0.06]">
                  <p className="text-sm font-semibold">Travel & Stay</p>
                </div>
                <div className="px-5 py-4 border-b border-white/[0.05]">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#64748B] mb-3 flex items-center gap-1.5">
                    <Hotel className="w-3 h-3" strokeWidth={1.5} /> Hotel
                  </p>
                  {!data?.accommodations?.length ? (
                    accommodationOpen ? (
                      <Link to={(user?.nationality !== "India" && !hasPaidAccommodation && !unpaidAccommodation) ? `/accommodation-choice${unpaidReg ? `?cart_reg_id=${unpaidReg.registration_id}` : ""}` : "/accommodation"} className="py-3 -my-3 text-xs text-[#00F0FF] hover:text-white transition-colors flex items-center gap-1 cursor-pointer">
                        {user?.nationality !== "India" ? "Select accommodation" : "Book accommodation"} <ChevronRight className="w-3 h-3" />
                      </Link>
                    ) : (
                      <p className="text-xs text-[#64748B]">Not open yet</p>
                    )
                  ) : data.accommodations.map((a) => (
                    <div key={a.booking_id}>
                      <p className="text-sm font-medium pr-4">{a.hotel_name}</p>
                      {a.nights > 0 ? (
                        <p className="text-xs text-[#94A3B8] mt-0.5">{a.room_type} · {a.nights} nights · <span className="tabular-nums">{a.currency === "INR" ? "₹" : "$"}{a.total_amount_usd}</span></p>
                      ) : (
                        <p className="text-xs text-[#94A3B8] mt-0.5"><span className="tabular-nums">{a.currency === "INR" ? "₹" : "$"}{a.total_amount_usd}</span></p>
                      )}
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4">
                  <p className="text-[11px] uppercase tracking-[0.15em] text-[#64748B] mb-3 flex items-center gap-1.5">
                    <Plane className="w-3 h-3" strokeWidth={1.5} /> Logistics
                  </p>
                  {!data?.logistics?.length ? (
                    logisticsOpen ? (
                      <Link to="/logistics" className="py-3 -my-3 text-xs text-[#00F0FF] hover:text-white transition-colors flex items-center gap-1 cursor-pointer">
                        Request pickup <ChevronRight className="w-3 h-3" />
                      </Link>
                    ) : (
                      <p className="text-xs text-[#64748B]">Not open yet</p>
                    )
                  ) : data.logistics.map((l) => (
                    <div key={l.request_id}>
                      <p className="text-sm font-medium">{l.service_type.replace(/_/g, " ")}</p>
                      <p className="text-xs text-[#94A3B8] mt-0.5">
                        {l.date_time} · <span className="tabular-nums">${l.amount_usd}</span>{" "}
                        <span className={l.status === "approved" ? "text-[#10B981]" : ""}>{l.status}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Notifications */}
              <motion.div
                {...fade(0.42)}
                className="rounded-2xl bg-[#142040]/40 backdrop-blur-sm border border-white/[0.08] overflow-hidden"
              >
                <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-3">
                  <Bell className="w-4 h-4 text-[#94A3B8]" strokeWidth={1.5} />
                  <p className="text-sm font-semibold">Notifications</p>
                  {data?.notifications?.length > 0 && (
                    <span className="ml-auto text-[11px] tabular-nums bg-[#00F0FF]/10 text-[#00F0FF] px-2 py-0.5 rounded-full font-semibold">
                      {data.notifications.length}
                    </span>
                  )}
                </div>
                <div className="divide-y divide-white/[0.05] max-h-60 overflow-y-auto">
                  {!data?.notifications?.length ? (
                    <p className="px-5 py-8 text-center text-sm text-[#64748B]">All caught up.</p>
                  ) : data.notifications.map((n) => (
                    <div key={n.notification_id} className="px-5 py-3 hover:bg-white/[0.02] transition-colors duration-150">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#00F0FF]/60 shrink-0" />
                        <div>
                          <p className="text-sm font-medium">{n.title}</p>
                          <p className="text-xs text-[#94A3B8] mt-0.5 leading-relaxed">{n.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* ── My Event Standings — coaches/managers have no individual
                events, so there's nothing to show standings for ── */}
            {!isNonAthlete && (
            <motion.div
              {...fade(0.48)}
              className="relative rounded-2xl overflow-hidden border border-[#00F0FF]/15"
              style={{ background: "linear-gradient(135deg, rgba(0,240,255,0.05) 0%, rgba(20,32,64,0.6) 60%)" }}
            >
              {/* Ambient glow */}
              <div className="absolute top-0 right-0 w-48 h-32 bg-[#00F0FF]/[0.07] blur-3xl pointer-events-none rounded-full" />

              <div className="relative flex items-center justify-between gap-3 px-6 py-5 border-b border-white/[0.06]">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex items-center justify-center shrink-0">
                    <BarChart2 className="w-4 h-4 text-[#00F0FF]" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00F0FF]">Live Standings</p>
                    <p className="text-base font-bold text-white leading-tight">My Event Results</p>
                  </div>
                </div>
                <Link
                  to="/results"
                  className="shrink-0 flex items-center gap-1.5 h-8 px-4 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20 text-[#00F0FF] text-xs font-semibold hover:bg-[#00F0FF]/20 transition-colors duration-150 touch-manipulation"
                >
                  Full results <ChevronRight className="w-3 h-3" />
                </Link>
              </div>

              {(data?.my_standings?.length > 0 || allEvents.length > 0) ? (
                <div className="relative">
                  <div className="grid grid-cols-[1fr_60px_76px] sm:grid-cols-[1fr_90px_100px] gap-3 px-4 sm:px-6 py-3 border-b border-white/[0.04]">
                    {["Event", "Rank", "Time"].map((h, i) => (
                      <span key={h} className={`text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B] ${i > 0 ? "text-right" : ""}`}>{h}</span>
                    ))}
                  </div>
                  <div className="divide-y divide-white/[0.04]">
                    {(data?.my_standings || allEvents.map(e => ({ event: e, rank: null, total: null, time: null }))).map((s, i) => {
                      const rankColor = s.rank === 1 ? "text-[#FBBF24]" : s.rank === 2 ? "text-[#94A3B8]" : s.rank === 3 ? "text-[#F97316]" : "text-white";
                      const sched = eventSchedule[s.event];
                      return (
                        <div key={s.event || i} className="grid grid-cols-[1fr_60px_76px] sm:grid-cols-[1fr_90px_100px] gap-3 px-4 sm:px-6 py-4 hover:bg-white/[0.02] transition-colors duration-150">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate">{s.event}</p>
                            {sched ? (
                              <p className="text-[11px] text-[#64748B] mt-0.5 tabular-nums truncate">
                                {sched.label} · {sched.time} · <span className="capitalize">{sched.session}</span>
                              </p>
                            ) : (
                              <p className="text-[11px] text-[#3A4A6A] mt-0.5">Schedule TBD</p>
                            )}
                          </div>
                          <span className={`text-sm font-bold text-right tabular-nums self-center ${s.rank ? rankColor : "text-[#3A4A6A]"}`}>
                            {s.rank ? `${s.rank}${s.total ? ` / ${s.total}` : ""}` : "—"}
                          </span>
                          <span className={`text-xs sm:text-sm font-mono font-bold text-right tabular-nums self-center ${s.time ? "text-[#00F0FF]" : "text-[#3A4A6A]"}`}>
                            {s.time || "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="relative px-6 py-10 text-center">
                  <p className="text-sm font-medium text-[#64748B]">Your standings will appear here once competition begins.</p>
                  {allRegs.length === 0 && !ineligible && !docsPending && eventRegistrationOpen && (
                    <Link to="/events" className="mt-3 py-3 inline-flex items-center gap-1 text-xs text-[#00F0FF] hover:text-white transition-colors">
                      Register for events <ChevronRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              )}
            </motion.div>
            )}

            {/* ── My Heat Lists ── */}
            {!isNonAthlete && (
            <motion.div
              {...fade(0.5)}
              className="relative rounded-2xl overflow-hidden border border-[#10B981]/15"
              style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.05) 0%, rgba(20,32,64,0.6) 60%)" }}
            >
              {/* Ambient glow */}
              <div className="absolute top-0 right-0 w-48 h-32 bg-[#10B981]/[0.07] blur-3xl pointer-events-none rounded-full" />

              <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-4 px-6 py-5 border-b border-white/[0.06]">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-[#10B981]/10 border border-[#10B981]/20 flex items-center justify-center shrink-0">
                    <List className="w-4 h-4 text-[#10B981]" strokeWidth={1.5} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#10B981]">Start Lists</p>
                    <p className="text-base font-bold text-white leading-tight">Heat & Lane Assignments</p>
                  </div>
                </div>
              </div>

              <div className="relative">
                {loadingHeats ? (
                  <div className="px-6 py-10 text-center text-sm font-medium text-[#64748B]">Loading…</div>
                ) : heatlists.length > 0 ? (
                  <>
                    <div className="grid grid-cols-[50px_1fr_60px] sm:grid-cols-[60px_1fr_70px] gap-3 px-4 sm:px-6 py-3 border-b border-white/[0.04]">
                      {["Lane", "Event", "Heat"].map((h, i) => (
                        <span key={h} className={`text-[11px] font-semibold uppercase tracking-[0.14em] text-[#64748B] ${i > 0 ? "text-right" : ""}`}>{h}</span>
                      ))}
                    </div>
                    <div className="divide-y divide-white/[0.04] max-h-96 overflow-y-auto">
                      {heatlists.map((h, i) => {
                        const sched = eventSchedule[h.event_name];
                        return (
                          <div key={i} className="grid grid-cols-[50px_1fr_60px] sm:grid-cols-[60px_1fr_70px] gap-3 px-4 sm:px-6 py-4 hover:bg-white/[0.02] transition-colors duration-150">
                            <span className="text-sm font-bold tabular-nums self-center text-[#FBBF24]">
                              {h.lane}
                            </span>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-white truncate">{h.event_name}</p>
                              <p className="text-[11px] text-[#00F0FF] mt-0.5 truncate">{h.day ? `Day ${h.day} · ` : ""}{h.athlete} ({h.team})</p>
                            </div>
                            <span className="text-sm font-bold text-right tabular-nums self-center text-white">
                              {h.heat} <span className="text-[10px] text-[#64748B] font-normal">/ {h.total_heats}</span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div className="px-6 py-10 text-center">
                    <p className="text-sm font-medium text-[#64748B]">
                      Your heat & lane assignments will appear here once published.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ── */

const Pill = ({ color, children }) => {
  const cls = {
    green:  "bg-[#10B981]/10 border-[#10B981]/25 text-[#10B981]",
    yellow: "bg-[#FBBF24]/10 border-[#FBBF24]/25 text-[#FBBF24]",
    red:    "bg-[#EF4444]/10 border-[#EF4444]/25 text-[#EF4444]",
  };
  const dotCls = { green: "bg-[#10B981]", yellow: "bg-[#FBBF24]", red: "bg-[#EF4444]" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold uppercase tracking-wider ${cls[color]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dotCls[color]}`} />
      {children}
    </span>
  );
};

const AlertBanner = ({ color, title, children }) => {
  const s = {
    yellow: { wrap: "bg-[#FBBF24]/[0.06] border-[#FBBF24]/20", icon: "text-[#FBBF24]", title: "text-[#FBBF24]" },
    red:    { wrap: "bg-[#EF4444]/[0.06] border-[#EF4444]/20",  icon: "text-[#EF4444]",  title: "text-[#EF4444]" },
  };
  return (
    <div className={`flex items-start gap-3 p-4 rounded-2xl border ${s[color].wrap}`}>
      <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${s[color].icon}`} strokeWidth={1.5} />
      <div>
        <p className={`text-sm font-semibold ${s[color].title}`}>{title}</p>
        <p className="text-xs text-[#94A3B8] mt-0.5 leading-relaxed">{children}</p>
      </div>
    </div>
  );
};
